import { useEffect, useRef, useState } from "react";
import { ImagePlus, Package, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

const FIELDS = [
  { key: "hero_title", label: "Hero Headline", rows: 2 },
  { key: "hero_subtitle", label: "Hero Description", rows: 3 },
  { key: "about_title", label: "About Us — Title", rows: 2 },
  { key: "about_body", label: "About Us — Content", rows: 5 },
  { key: "rewards_title", label: "Rewards Preview — Title", rows: 2 },
  { key: "rewards_body", label: "Rewards Preview — Content", rows: 4 },
  { key: "products_title", label: "Products Preview — Title", rows: 2 },
  { key: "products_body", label: "Products Preview — Content", rows: 4 },
  { key: "ai_title", label: "PlusMaker AI — Title", rows: 2 },
  { key: "ai_body", label: "PlusMaker AI — Content", rows: 4 },
  { key: "cta_title", label: "Final CTA — Title", rows: 2 },
  { key: "cta_body", label: "Final CTA — Content", rows: 3 },
];

const BADGE_SUGGESTIONS = ["SOON", "BEST PRODUCT", "FEATURED", "NEW", "AVAILABLE"];

const EMPTY_SLOTS = [1, 2, 3, 4, 5].map((position) => ({
  name: "", description: "", image_url: "", badge: "SOON", position,
}));

export default function AdminContent() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [error, setError] = useState("");
  const fileRefs = useRef({});

  useEffect(() => {
    api
      .get("/admin/site-content")
      .then((res) => {
        const data = res.data;
        if (!Array.isArray(data.products) || data.products.length !== 5) {
          data.products = EMPTY_SLOTS;
        }
        setContent(data);
      })
      .catch((err) => setError(formatApiError(err)));
  }, []);

  const handleChange = (key) => (e) =>
    setContent((c) => ({ ...c, [key]: e.target.value }));

  const updateSlot = (index, key, value) =>
    setContent((c) => ({
      ...c,
      products: c.products.map((p, i) => (i === index ? { ...p, [key]: value } : p)),
    }));

  const uploadSlotImage = async (index, file) => {
    if (!file) return;
    setUploadingSlot(index);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/uploads/image", fd);
      updateSlot(index, "image_url", res.data.url);
      toast.success("Product image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setUploadingSlot(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      await api.put("/admin/site-content", content);
      toast.success("Website content updated");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (error && !content) {
    return <p className="text-sm text-red-400" data-testid="site-content-error">{error}</p>;
  }
  if (!content) {
    return <p className="text-sm text-[#64748B]" data-testid="site-content-loading">Loading content...</p>;
  }

  const slots = [...content.products].sort((a, b) => (a.position || 0) - (b.position || 0));

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-content-page">
      <div>
        <p className="eyebrow-label mb-2">CMS</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">Website Content</h1>
        <p className="mt-2 text-sm text-[#64748B]">
          Edit the public landing page copy. Changes go live immediately after saving.
        </p>
      </div>

      <div className="card-lux p-8 space-y-6">
        {FIELDS.map((field) => (
          <div key={field.key}>
            <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">
              {field.label}
            </label>
            <textarea
              rows={field.rows}
              value={content[field.key] || ""}
              onChange={handleChange(field.key)}
              className="input-lux resize-y"
              data-testid={`content-field-${field.key.replace(/_/g, "-")}`}
            />
          </div>
        ))}
      </div>

      <div className="card-lux p-8 space-y-6" data-testid="products-slots-editor">
        <div>
          <p className="text-sm font-semibold text-white flex items-center gap-2">
            <Package className="h-4 w-4 text-[#D4AF37]" /> Products Preview — 5 Product Slots
          </p>
          <p className="mt-1 text-xs text-[#64748B]">
            Each slot renders as a card on the landing page. Position controls display order.
          </p>
        </div>

        {slots.map((slot) => {
          const index = content.products.indexOf(slot);
          return (
            <div key={index} className="rounded-xl border border-[#222530] p-5 space-y-4" data-testid={`product-slot-${index}`}>
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-mono tracking-widest uppercase text-[#D4AF37]">Product {index + 1}</p>
                <div className="flex items-center gap-2">
                  <label className="text-[10px] font-mono tracking-widest uppercase text-[#64748B]">Position</label>
                  <input
                    type="number" min="1" max="5"
                    value={slot.position}
                    onChange={(e) => updateSlot(index, "position", Number(e.target.value))}
                    className="input-lux !py-1.5 !px-3 w-20 text-xs"
                    data-testid={`product-position-${index}`}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Product Image / Icon</label>
                <input
                  ref={(el) => { fileRefs.current[index] = el; }}
                  type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                  onChange={(e) => { uploadSlotImage(index, e.target.files?.[0]); e.target.value = ""; }}
                  data-testid={`product-image-input-${index}`}
                />
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => fileRefs.current[index]?.click()}
                    disabled={uploadingSlot === index}
                    className="btn-gold-outline px-4 py-2 text-xs flex items-center gap-2 disabled:opacity-50"
                    data-testid={`product-image-upload-${index}`}
                  >
                    <ImagePlus className="h-3.5 w-3.5" /> {uploadingSlot === index ? "Uploading…" : "Upload Image"}
                  </button>
                  {slot.image_url ? (
                    <>
                      <img src={`${API}${slot.image_url}`} alt={slot.name || "Product"} className="h-12 w-12 object-cover rounded-md border border-[#D4AF37]/40" data-testid={`product-image-preview-${index}`} />
                      <button type="button" onClick={() => updateSlot(index, "image_url", "")} className="text-[#94A3B8] hover:text-red-400 p-1 transition-colors" data-testid={`product-image-remove-${index}`} aria-label="Remove image">
                        <X className="h-4 w-4" />
                      </button>
                    </>
                  ) : (
                    <span className="text-[11px] text-[#64748B]">No image — a gold package icon is shown</span>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Product Name</label>
                <input
                  className="input-lux"
                  value={slot.name}
                  onChange={(e) => updateSlot(index, "name", e.target.value)}
                  placeholder="e.g. Smart Order Flow"
                  data-testid={`product-name-${index}`}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Short Description</label>
                <textarea
                  rows={2}
                  className="input-lux resize-y"
                  value={slot.description}
                  onChange={(e) => updateSlot(index, "description", e.target.value)}
                  placeholder="One professional line about this product"
                  data-testid={`product-description-${index}`}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-2 tracking-wide">Status Badge</label>
                <input
                  className="input-lux"
                  value={slot.badge}
                  onChange={(e) => updateSlot(index, "badge", e.target.value)}
                  placeholder="SOON"
                  data-testid={`product-badge-${index}`}
                />
                <div className="mt-2 flex flex-wrap gap-2">
                  {BADGE_SUGGESTIONS.map((b) => (
                    <button
                      key={b}
                      type="button"
                      onClick={() => updateSlot(index, "badge", b)}
                      className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 border transition-colors ${slot.badge === b ? "text-black bg-[#D4AF37] border-[#D4AF37]" : "text-[#D4AF37] border-[#D4AF37]/30 hover:bg-[#D4AF37]/10"}`}
                      data-testid={`product-badge-chip-${index}-${b.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      {b}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          );
        })}

        {error && (
          <p className="text-sm text-red-400 bg-red-400/10 border border-red-400/25 rounded-lg px-4 py-3" data-testid="site-content-save-error">
            {error}
          </p>
        )}

        <button
          onClick={handleSave}
          disabled={saving || uploadingSlot !== null}
          className="btn-gold px-8 py-3 text-sm disabled:opacity-60"
          data-testid="site-content-save-button"
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
}
